import React, { useEffect, useState } from "react";
import { api, downloadAuthFile, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
} from "recharts";

const COLORS = ["#0055FF", "#111111", "#34C759", "#FF3B30", "#9CA3AF"];

const StatCard = ({ label, value, sub }) => (
  <div className="border border-black/10 p-6 bg-white">
    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">{label}</div>
    <div className="mt-3 font-display font-black text-4xl tracking-tight">{value}</div>
    {sub && <div className="mt-1 text-xs font-mono text-slate-500">{sub}</div>}
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const isFullAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data));
  }, []);

  if (!data)
    return <div className="text-sm font-mono text-slate-500">Loading…</div>;

  return (
    <div data-testid="admin-dashboard">
      <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">Overview</div>
      <div className="mt-2 flex items-end justify-between flex-wrap gap-4">
        <h1 className="font-display font-extrabold text-4xl tracking-tight">Sales console</h1>
        {isFullAdmin && (
          <button
            type="button"
            onClick={async () => {
              try {
                await downloadAuthFile("/admin/report.csv", "shamstrade-sales-report.csv");
              } catch (e) {
                toast.error(formatApiError(e?.response?.data?.detail) || "Download failed");
              }
            }}
            data-testid="download-sales-report"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-ink text-white hover:bg-electric text-xs font-mono uppercase tracking-[0.2em]"
          >
            ⬇ Download sales report (CSV)
          </button>
        )}
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Revenue" value={`${data.totals.revenue.toFixed(2)}`} sub="JOD lifetime" />
        <StatCard label="Orders" value={data.totals.orders} sub="All-time" />
        <StatCard label="Customers" value={data.totals.customers} sub="Registered" />
        <StatCard label="Products" value={data.totals.products} sub="Live SKUs" />
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 border border-black/10 bg-white p-6">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500 mb-4">
            Revenue trend (recent)
          </div>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={data.sales_chart}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="0" vertical={false} />
                <XAxis dataKey="day" stroke="#6b7280" fontSize={11} fontFamily="IBM Plex Mono" />
                <YAxis stroke="#6b7280" fontSize={11} fontFamily="IBM Plex Mono" />
                <Tooltip
                  contentStyle={{
                    background: "#111111",
                    border: "none",
                    color: "#ffffff",
                    fontFamily: "IBM Plex Mono",
                    fontSize: 12,
                  }}
                />
                <Line
                  type="stepAfter"
                  dataKey="revenue"
                  stroke="#0055FF"
                  strokeWidth={2.5}
                  dot={{ fill: "#111111", r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-black/10 bg-white p-6">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500 mb-4">
            Inventory by brand
          </div>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={data.brand_chart}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="0" vertical={false} />
                <XAxis dataKey="brand" stroke="#6b7280" fontSize={11} fontFamily="IBM Plex Mono" />
                <YAxis stroke="#6b7280" fontSize={11} fontFamily="IBM Plex Mono" />
                <Tooltip
                  contentStyle={{
                    background: "#111111",
                    border: "none",
                    color: "#ffffff",
                    fontFamily: "IBM Plex Mono",
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="count">
                  {data.brand_chart.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-10 border border-black/10 bg-white p-6">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500 mb-4">
          Order status breakdown
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {["pending", "confirmed", "shipped", "delivered", "cancelled"].map((s) => {
            const item = data.status_chart.find((x) => x.status === s);
            return (
              <div key={s} className="border border-black/10 p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{s}</div>
                <div className="mt-2 font-display font-black text-2xl">{item?.count || 0}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
